'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import { compose } from 'redux';
import { connect } from 'react-redux';
import text from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import * as CustomWidgetEditorOptionsActions from 'DesignData/actions/CustomWidgetEditorOptions';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
import { getIsUngatedForContentTypes } from 'DesignData/selectors/gates';
import { NAME_LABEL_ICON_SIZE } from 'DesignData/constants/Icons';
import UIFlex from 'UIComponents/layout/UIFlex';
import { ANY, NONE } from 'DesignData/enums/CustomWidgetHostTemplateTypes';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UIHoverlay from 'UIComponents/overlay/UIHoverlay';
import UIButton from 'UIComponents/button/UIButton';
import UITextInput from 'UIComponents/input/UITextInput';
import UIFormControl from 'UIComponents/form/UIFormControl';
import Small from 'UIComponents/elements/Small';
import WidgetIcon from 'ContentComponents/cmv2/WidgetIcon';
import makeAsyncModal from 'DesignComponents/modals/makeAsyncModal';
import withDebouncedInput from 'DesignEditorsUI/components/shared/inputs/withDebouncedInput';
import ValidatedNameLabel from 'DesignEditorsUI/components/shared/inputs/ValidatedNameLabel';
import withUpdateTemplateTypeModal from 'DesignEditorsUI/components/modals/hoc/withUpdateTemplateTypeModal';
const RenameFileConfirmModal = makeAsyncModal(() => import(
/* webpackChunkName: "modals" */
'DesignComponents/modals/RenameFileConfirmModal'));
const DebouncedUITextInput = withDebouncedInput(UITextInput);

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.main.nameLabel.${key}`;

const EMPTY_ARRAY = Object.freeze([]);

function makeMapStateToProps() {
  const getCustomWidgetBuffer = customWidgetBuffersSelectors.makeGetCustomWidgetBuffer();
  return (state, props) => {
    const customWidgetBuffer = getCustomWidgetBuffer(state, props);
    let customWidgetLabel = '';

    if (customWidgetBuffer) {
      if (customWidgetBuffer.path && customWidgetBuffer.path.startsWith('@hubspot')) {
        customWidgetLabel = customWidgetBuffer.displayName || customWidgetBuffer.label || customWidgetBuffer.name;
      } else {
        customWidgetLabel = customWidgetBuffer.name || '';
      }
    }

    return {
      customWidgetLabel,
      displayName: customWidgetBuffer ? customWidgetBuffer.displayName || '' : '',
      isV1: customWidgetBuffer ? customWidgetBuffer.schemaVersion === 1 : false,
      isUngatedForContentTypes: getIsUngatedForContentTypes(state),
      contentTypes: customWidgetBuffer && customWidgetBuffer.contentTypes ? customWidgetBuffer.contentTypes : EMPTY_ARRAY,
      hostTemplateTypes: customWidgetBuffer ? customWidgetBuffer.hostTemplateTypes : EMPTY_ARRAY,
      customWidgetIcon: customWidgetBuffer ? customWidgetBuffer.icon : null
    };
  };
}

const mapDispatchToProps = {
  setIsSelectingIcon: CustomWidgetEditorOptionsActions.setIsSelectingIcon
};

class NameLabel extends PureComponent {
  constructor(props) {
    super(props);

    this.onLabelBlur = () => {
      const initialLabel = this.props.customWidgetLabel;
      const {
        isValidLabel,
        label
      } = this.state;

      if (isValidLabel && label !== initialLabel) {
        this.handleRenameConfirmOpen();
      } else {
        this.setState({
          isValidLabel: true,
          label: initialLabel
        });
      }
    };

    this.handleRenameConfirmOpen = () => {
      this.setState({
        showRenameConfirmModal: true
      });
    };

    this.handleRenameConfirmClose = () => {
      this.setState({
        showRenameConfirmModal: false
      });
    };

    this.handleRenameConfirmCallback = (confirmed = false) => {
      if (!confirmed) {
        this.onLabelChange(this.props.customWidgetLabel, true);
      }

      this.handleRenameConfirmClose();
    };

    this.handleDisplayNameChange = event => {
      const {
        updateCustomWidget
      } = this.props;
      updateCustomWidget({
        displayName: event.target.value
      });
    };

    this.onLabelChange = (label, isValidLabel) => {
      this.setState({
        isValidLabel,
        label
      });
    };

    this.onLabelError = label => {
      this.onLabelChange(label, false);
    };

    this.onLabelSuccess = label => {
      this.onLabelChange(label, true);
    };

    this.onIconClick = () => {
      const {
        customWidgetId,
        setIsSelectingIcon
      } = this.props;
      setIsSelectingIcon(customWidgetId, true);
    };

    const {
      customWidgetLabel
    } = this.props;
    this.state = {
      isValidLabel: true,
      label: customWidgetLabel,
      showRenameConfirmModal: false
    };
  }

  componentDidUpdate(prevProps) {
    const initialLabel = prevProps.customWidgetLabel;

    if (initialLabel !== this.props.customWidgetLabel) {
      this.setState({
        label: this.props.customWidgetLabel
      });
    }
  }

  renderRenameFileConfirmModal() {
    if (!this.state.showRenameConfirmModal) {
      return null;
    }

    return /*#__PURE__*/_jsx(RenameFileConfirmModal, {
      callback: this.handleRenameConfirmCallback,
      fileId: this.props.customWidgetId,
      newFileName: this.state.label
    });
  }

  renderTypeLabel() {
    const {
      isReadOnly,
      isV1,
      isUngatedForContentTypes,
      contentTypes,
      hostTemplateTypes,
      revisionId,
      onUpdateHostTemplateTypesClick
    } = this.props;

    if (isV1) {
      return null;
    }

    if (isUngatedForContentTypes) {
      let typesList;
      let labelKey = 'contentTypesLabel';

      if (!contentTypes.length || contentTypes.includes(ANY)) {
        typesList = /*#__PURE__*/_jsx(FormattedMessage, {
          message: !contentTypes.length ? i18nKey('contentTypes.noContentTypes') : `contentUtils.contentTypes.${ANY}`
        }); // Force plural template types label for any/none-type modules

        labelKey = 'contentTypesLabel.other';
      } else {
        typesList = contentTypes.map(type => text(`contentUtils.contentTypes.${type}`)).join(', ');
      }

      return /*#__PURE__*/_jsx(Small, {
        className: "display-block",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: i18nKey(labelKey),
          options: {
            count: contentTypes.length,
            types: /*#__PURE__*/_jsx(UIButton, {
              onClick: onUpdateHostTemplateTypesClick,
              use: "link",
              size: "extra-small",
              disabled: !!revisionId || isReadOnly,
              children: typesList
            })
          }
        })
      });
    }

    const templateTypesCount = hostTemplateTypes.includes(ANY) || hostTemplateTypes.includes(NONE) ? hostTemplateTypes.length + 1 : hostTemplateTypes.length;
    return /*#__PURE__*/_jsx(Small, {
      className: "display-block",
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: i18nKey('templateTypesLabel'),
        options: {
          count: templateTypesCount,
          types: /*#__PURE__*/_jsx(UIButton, {
            onClick: onUpdateHostTemplateTypesClick,
            use: "link",
            size: "extra-small",
            disabled: !!revisionId || isReadOnly,
            children: hostTemplateTypes.map(type => text(i18nKey(`types.${type}`))).join(', ')
          })
        }
      })
    });
  }

  renderIcon() {
    const {
      isReadOnly,
      customWidgetIcon,
      isV1
    } = this.props;
    const iconClassNames = !customWidgetIcon ? 'p-y-2' : "";

    let icon = /*#__PURE__*/_jsx(WidgetIcon, {
      className: iconClassNames,
      size: NAME_LABEL_ICON_SIZE,
      icon: customWidgetIcon
    });

    if (!isReadOnly && !isV1) {
      const foregroundContent = /*#__PURE__*/_jsx(UIFlex, {
        align: "center",
        justify: "center",
        children: /*#__PURE__*/_jsx("div", {
          className: "change-icon-text text-center",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('changeIcon')
          })
        })
      });

      icon = /*#__PURE__*/_jsx(UIHoverlay, {
        className: "custom-widget-icon-overlay",
        foregroundContent: foregroundContent,
        onClick: this.onIconClick,
        children: icon
      });
    }

    return /*#__PURE__*/_jsx("div", {
      className: "m-right-2",
      children: icon
    });
  }

  render() {
    const {
      label
    } = this.state;
    const {
      displayName
    } = this.props;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsxs(UIMediaObject, {
        align: "center",
        className: "custom-widget-name-label",
        itemLeft: this.renderIcon(),
        children: [this.renderRenameFileConfirmModal(), this.props.updateTemplateModal, /*#__PURE__*/_jsx(ValidatedNameLabel, {
          allowBlank: false,
          onBlur: this.onLabelBlur,
          onError: this.onLabelError,
          onSuccess: this.onLabelSuccess,
          placeholder: text(i18nKey('placeholder')),
          readOnly: this.props.isReadOnly,
          typeLabel: this.renderTypeLabel(),
          value: label
        })]
      }), /*#__PURE__*/_jsx(UIFormControl, {
        className: "m-top-2",
        "data-test-id": "module-label",
        tooltip: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: i18nKey('displayNameTooltip')
        }),
        label: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: i18nKey('displayNameLabel')
        }),
        children: /*#__PURE__*/_jsx(DebouncedUITextInput, {
          readOnly: this.props.isReadOnly,
          value: displayName,
          onChange: this.handleDisplayNameChange
        })
      })]
    });
  }

}

NameLabel.propTypes = {
  customWidgetLabel: PropTypes.string.isRequired,
  customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
  displayName: PropTypes.string.isRequired,
  revisionId: PropTypes.number,
  isReadOnly: PropTypes.bool.isRequired,
  isUngatedForContentTypes: PropTypes.bool.isRequired,
  isV1: PropTypes.bool.isRequired,
  contentTypes: PropTypes.array.isRequired,
  hostTemplateTypes: PropTypes.array.isRequired,
  onUpdateHostTemplateTypesClick: PropTypes.func.isRequired,
  updateTemplateModal: PropTypes.node,
  customWidgetIcon: PropTypes.string,
  setIsSelectingIcon: PropTypes.func.isRequired,
  updateCustomWidget: PropTypes.func.isRequired
};
export default compose(connect(makeMapStateToProps, mapDispatchToProps), withUpdateTemplateTypeModal)(NameLabel);